import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { 
  MessageSquare, 
  Heart, 
  Brain, 
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Target,
  Activity
} from 'lucide-react';

interface FeedbackResponse {
  feedback_id: string;
  processed_at: string;
  actions_taken: string[];
  results: any[];
  learning_impact: {
    signal_count: number;
    high_confidence_signals: number;
    categories_affected: string[];
    learning_value: string;
  };
}

interface UserPreferences {
  preferences: Array<{
    preference_type: string;
    preference_value: string;
    confidence: number;
    created_at: string;
  }>;
  constraints: Array<{
    constraint_type: string;
    constraint_value: string;
    confidence: number;
    is_active: boolean;
    created_at: string;
  }>;
  recent_feedback: Array<{
    feedback_type: string;
    content: string;
    urgency: string;
    source: string;
    created_at: string;
  }>;
}

interface FeedbackInsights {
  total_feedback_count: number;
  feedback_by_type: Record<string, number>;
  recent_patterns: Array<{
    pattern_type: string;
    pattern_description: string;
    confidence: number;
    last_updated_at: string;
  }>;
  adaptation_effectiveness: {
    total_adaptations: number;
    confirmed_adaptations: number;
    avg_confidence: number;
  };
}

const FeedbackIntelligenceDemo: React.FC = () => {
  const [activeTab, setActiveTab] = useState('submit');
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackType, setFeedbackType] = useState('life_context_change');
  const [urgency, setUrgency] = useState('routine');
  const [source, setSource] = useState('demo');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastResponse, setLastResponse] = useState<FeedbackResponse | null>(null);
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [insights, setInsights] = useState<FeedbackInsights | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Sample feedback scenarios
  const sampleFeedbacks = [
    {
      type: 'life_context_change',
      content: "I need to travel for work next week and can only do 3 workouts instead of 5. Can we adjust my plan?",
      urgency: 'urgent',
      description: "Schedule conflict requiring plan adaptation"
    },
    {
      type: 'workout_difficulty',
      content: "That last workout was way too hard. I couldn't complete the last 2 sets and felt dizzy.",
      urgency: 'important',
      description: "Workout intensity feedback"
    },
    {
      type: 'exercise_form',
      content: "I'm not sure I'm doing the deadlifts correctly. My lower back feels tight after.",
      urgency: 'important',
      description: "Form and safety concern"
    },
    {
      type: 'nutrition_adherence',
      content: "I've been craving sweets a lot lately and it's hard to stick to the meal plan.",
      urgency: 'routine',
      description: "Nutrition adherence challenge"
    },
    {
      type: 'sleep_quality',
      content: "I've been sleeping poorly this week - only 5-6 hours per night. Feeling exhausted.",
      urgency: 'urgent',
      description: "Sleep and recovery issue"
    },
    {
      type: 'goal_progress',
      content: "I'm really happy with my progress! I can finally do 10 push-ups in a row!",
      urgency: 'routine',
      description: "Positive progress feedback"
    }
  ];

  const feedbackTypes = [
    'workout_completion', 'workout_difficulty', 'workout_enjoyment',
    'exercise_form', 'exercise_substitution', 'recovery_status', 'injury_report',
    'plan_adherence', 'schedule_conflict', 'plan_progression', 'plan_modification_request',
    'meal_completion', 'meal_enjoyment', 'nutrition_adherence', 'dietary_restriction',
    'sleep_quality', 'sleep_duration', 'recovery_feeling', 'stress_level',
    'resource_relevance', 'content_difficulty', 'creator_preference', 'learning_preference',
    'coaching_style', 'communication_preference', 'motivation_effectiveness',
    'feature_usage', 'user_satisfaction', 'goal_progress', 'life_context_change'
  ];

  const urgencyLevels = ['immediate', 'urgent', 'important', 'routine'];

  const submitFeedback = async () => {
    if (!feedbackText.trim()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/feedback/general', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          feedback_type: feedbackType,
          content: feedbackText,
          urgency: urgency,
          structured_data: {},
          context: { demo: true, timestamp: new Date().toISOString() },
          source: source
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: FeedbackResponse = await response.json();
      setLastResponse(result);
      setFeedbackText('');
      
      // Refresh preferences and insights
      await Promise.all([loadPreferences(), loadInsights()]);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadPreferences = async () => {
    try {
      const response = await fetch('/feedback/preferences');
      if (response.ok) {
        const data: UserPreferences = await response.json();
        setPreferences(data);
      }
    } catch (err) {
      console.error('Failed to load preferences:', err);
    }
  };

  const loadInsights = async () => {
    try {
      const response = await fetch('/feedback/insights');
      if (response.ok) {
        const data: FeedbackInsights = await response.json();
        setInsights(data);
      }
    } catch (err) {
      console.error('Failed to load insights:', err);
    }
  };

  const applySampleFeedback = (sample: typeof sampleFeedbacks[0]) => {
    setFeedbackText(sample.content);
    setFeedbackType(sample.type);
    setUrgency(sample.urgency);
  };

  useEffect(() => {
    loadPreferences();
    loadInsights();
  }, []);

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'immediate': return 'bg-red-100 text-red-800';
      case 'urgent': return 'bg-orange-100 text-orange-800';
      case 'important': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getUrgencyIcon = (urgency: string) => {
    switch (urgency) {
      case 'immediate': return <AlertTriangle className="w-4 h-4" />;
      case 'urgent': return <Clock className="w-4 h-4" />;
      case 'important': return <Target className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">Feedback Intelligence System</h1>
        <p className="text-gray-600">
          Every interaction is feedback. Cedar learns from everything you share to provide better coaching.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="submit" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Submit Feedback
          </TabsTrigger>
          <TabsTrigger value="preferences" className="flex items-center gap-2">
            <Heart className="w-4 h-4" />
            Your Profile
          </TabsTrigger>
          <TabsTrigger value="insights" className="flex items-center gap-2">
            <Brain className="w-4 h-4" />
            Learning Insights
          </TabsTrigger>
          <TabsTrigger value="examples" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Examples
          </TabsTrigger>
        </TabsList>

        <TabsContent value="submit" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Share Your Feedback
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Feedback Type</label>
                  <select 
                    value={feedbackType} 
                    onChange={(e) => setFeedbackType(e.target.value)}
                    className="w-full p-2 border rounded-md"
                  >
                    {feedbackTypes.map(type => (
                      <option key={type} value={type}>
                        {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Urgency</label>
                  <select 
                    value={urgency} 
                    onChange={(e) => setUrgency(e.target.value)}
                    className="w-full p-2 border rounded-md"
                  >
                    {urgencyLevels.map(level => (
                      <option key={level} value={level}>
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Source</label>
                  <select 
                    value={source} 
                    onChange={(e) => setSource(e.target.value)}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="demo">Demo</option>
                    <option value="chat">Chat</option>
                    <option value="plan_view">Plan View</option>
                    <option value="workout_completion">Workout</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Your Feedback</label>
                <Textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Tell Cedar what's on your mind..."
                  rows={4}
                />
              </div>

              <Button 
                onClick={submitFeedback} 
                disabled={isSubmitting || !feedbackText.trim()}
                className="w-full"
              >
                {isSubmitting ? 'Processing...' : 'Submit Feedback'}
              </Button>

              {error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              {lastResponse && (
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    <div className="space-y-2">
                      <p><strong>Feedback processed successfully!</strong></p>
                      <p>Actions taken: {lastResponse.actions_taken.join(', ')}</p>
                      <p>Learning impact: {lastResponse.learning_impact.learning_value}</p>
                      <p>Signals captured: {lastResponse.learning_impact.signal_count}</p>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quick Examples</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {sampleFeedbacks.map((sample, index) => (
                  <div 
                    key={index}
                    className="p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => applySampleFeedback(sample)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Badge className={getUrgencyColor(sample.urgency)}>
                        {getUrgencyIcon(sample.urgency)}
                        {sample.urgency}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        {sample.type.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mb-1">{sample.content}</p>
                    <p className="text-xs text-gray-500">{sample.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preferences" className="space-y-4">
          {preferences && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Heart className="w-5 h-5" />
                    Your Learned Preferences
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {preferences.preferences.length > 0 ? (
                    <div className="space-y-3">
                      {preferences.preferences.map((pref, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                          <div>
                            <p className="font-medium">{pref.preference_type.replace(/_/g, ' ')}</p>
                            <p className="text-sm text-gray-600">{pref.preference_value}</p>
                          </div>
                          <Badge variant="secondary">
                            {Math.round(pref.confidence * 100)}% confident
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">
                      No preferences learned yet. Share more feedback to help Cedar understand you better!
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Your Constraints
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {preferences.constraints.length > 0 ? (
                    <div className="space-y-3">
                      {preferences.constraints.map((constraint, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                          <div>
                            <p className="font-medium">{constraint.constraint_type.replace(/_/g, ' ')}</p>
                            <p className="text-sm text-gray-600">{constraint.constraint_value}</p>
                          </div>
                          <Badge variant="secondary">
                            {Math.round(constraint.confidence * 100)}% confident
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">
                      No constraints identified yet.
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recent Feedback</CardTitle>
                </CardHeader>
                <CardContent>
                  {preferences.recent_feedback.length > 0 ? (
                    <div className="space-y-3">
                      {preferences.recent_feedback.slice(0, 5).map((feedback, index) => (
                        <div key={index} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <Badge className={getUrgencyColor(feedback.urgency)}>
                              {feedback.urgency}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {new Date(feedback.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-sm">{feedback.content}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {feedback.feedback_type.replace(/_/g, ' ')} • {feedback.source}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">
                      No recent feedback.
                    </p>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="insights" className="space-y-4">
          {insights && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      {insights.total_feedback_count}
                    </div>
                    <p className="text-sm text-gray-600">Total Feedback</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl font-bold text-green-600 mb-2">
                      {insights.adaptation_effectiveness.total_adaptations || 0}
                    </div>
                    <p className="text-sm text-gray-600">Plan Adaptations</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl font-bold text-purple-600 mb-2">
                      {insights.recent_patterns.length}
                    </div>
                    <p className="text-sm text-gray-600">Learning Patterns</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Feedback by Type</CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.keys(insights.feedback_by_type).length > 0 ? (
                    <div className="space-y-2">
                      {Object.entries(insights.feedback_by_type)
                        .sort(([,a], [,b]) => b - a)
                        .map(([type, count]) => (
                          <div key={type} className="flex items-center justify-between">
                            <span className="text-sm">{type.replace(/_/g, ' ')}</span>
                            <Badge variant="outline">{count}</Badge>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">
                      No feedback data yet.
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Learning Patterns</CardTitle>
                </CardHeader>
                <CardContent>
                  {insights.recent_patterns.length > 0 ? (
                    <div className="space-y-3">
                      {insights.recent_patterns.map((pattern, index) => (
                        <div key={index} className="p-3 bg-purple-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium">{pattern.pattern_type.replace(/_/g, ' ')}</h4>
                            <Badge variant="secondary">
                              {Math.round(pattern.confidence * 100)}% confident
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600">{pattern.pattern_description}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">
                      No learning patterns identified yet.
                    </p>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="examples" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>How Cedar Learns From Your Feedback</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="p-4 border-l-4 border-blue-500 bg-blue-50">
                  <h4 className="font-semibold text-blue-800 mb-2">🏋️ Workout Feedback</h4>
                  <p className="text-sm text-blue-700 mb-2">
                    <strong>You say:</strong> "That workout was too easy, I barely broke a sweat."
                  </p>
                  <p className="text-sm text-blue-600">
                    <strong>Cedar learns:</strong> Increases intensity for future workouts, notes your fitness level is higher than estimated.
                  </p>
                </div>

                <div className="p-4 border-l-4 border-orange-500 bg-orange-50">
                  <h4 className="font-semibold text-orange-800 mb-2">📅 Schedule Feedback</h4>
                  <p className="text-sm text-orange-700 mb-2">
                    <strong>You say:</strong> "I can't do 5 workouts this week, I'm traveling for work."
                  </p>
                  <p className="text-sm text-orange-600">
                    <strong>Cedar learns:</strong> Immediately adapts your plan to 3 workouts, learns about your travel patterns for future planning.
                  </p>
                </div>

                <div className="p-4 border-l-4 border-green-500 bg-green-50">
                  <h4 className="font-semibold text-green-800 mb-2">🍎 Nutrition Feedback</h4>
                  <p className="text-sm text-green-700 mb-2">
                    <strong>You say:</strong> "I'm always hungry between meals, especially in the afternoon."
                  </p>
                  <p className="text-sm text-green-600">
                    <strong>Cedar learns:</strong> Adjusts meal timing and macros, suggests healthy afternoon snacks, monitors your hunger patterns.
                  </p>
                </div>

                <div className="p-4 border-l-4 border-purple-500 bg-purple-50">
                  <h4 className="font-semibold text-purple-800 mb-2">😴 Recovery Feedback</h4>
                  <p className="text-sm text-purple-700 mb-2">
                    <strong>You say:</strong> "I've been sleeping poorly and feeling exhausted."
                  </p>
                  <p className="text-sm text-purple-600">
                    <strong>Cedar learns:</strong> Reduces workout intensity, suggests recovery activities, asks about sleep hygiene.
                  </p>
                </div>

                <div className="p-4 border-l-4 border-red-500 bg-red-50">
                  <h4 className="font-semibold text-red-800 mb-2">🎯 Goal Feedback</h4>
                  <p className="text-sm text-red-700 mb-2">
                    <strong>You say:</strong> "I'm really happy with my progress! I can finally do 10 push-ups!"
                  </p>
                  <p className="text-sm text-red-600">
                    <strong>Cedar learns:</strong> Celebrates your milestone, adjusts progression targets, reinforces what's working well.
                  </p>
                </div>
              </div>

              <Alert>
                <Brain className="h-4 w-4" />
                <AlertDescription>
                  <strong>The key insight:</strong> Every message you send to Cedar is feedback at multiple levels. 
                  Cedar doesn't just respond to your words - it learns about your preferences, constraints, 
                  patterns, and progress to become a better coach for you over time.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FeedbackIntelligenceDemo;